import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, User, Phone, MapPin, Save, LogOut, Home as HomeIcon, Mail, X, Gift, ChevronRight } from 'lucide-react';
import IndividualBottomNav from '@/components/IndividualBottomNav';
import IndividualSidebar from '@/components/IndividualSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Address Search with Nominatim ────────────────────────────────────────────
const AddressSearch = ({ value, onChange, onSelect }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const timerRef = useRef(null);
  const containerRef = useRef(null);
  const selectingRef = useRef(false);
  const autocompleteService = useRef(null);
  const geocoderRef = useRef(null);

  useEffect(() => {
    const initGoogle = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        geocoderRef.current = new window.google.maps.Geocoder();
      }
    };
    if (window.google) { initGoogle(); }
    else {
      const interval = setInterval(() => {
        if (window.google) { initGoogle(); clearInterval(interval); }
      }, 300);
      return () => clearInterval(interval);
    }
  }, []);

  useEffect(() => { setQuery(value || ''); }, [value]);

  const search = useCallback((q) => {
    if (!q || q.length < 2) { setResults([]); setShowDropdown(false); return; }
    if (!autocompleteService.current) return;
    setSearching(true);
    const timeout = setTimeout(() => setSearching(false), 5000);
    autocompleteService.current.getPlacePredictions(
      { input: q, componentRestrictions: { country: 'in' }, types: ['geocode', 'establishment'] },
      (predictions, status) => {
        clearTimeout(timeout);
        setSearching(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
          setResults(predictions);
          setShowDropdown(true);
        } else {
          setResults([]);
          setShowDropdown(false);
        }
      }
    );
  }, []);

  useEffect(() => {
    const handleOutside = (e) => {
      if (selectingRef.current) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(v), 300);
  };

  const handleSelect = (prediction) => {
    selectingRef.current = false;
    const addr = prediction.description;
    setQuery(addr);
    onChange(addr);
    setShowDropdown(false);
    setResults([]);
    if (geocoderRef.current) {
      geocoderRef.current.geocode({ placeId: prediction.place_id }, (res, status) => {
        if (status === 'OK' && res[0]) {
          const loc = res[0].geometry.location;
          onSelect({ address: addr, lat: loc.lat(), lon: loc.lng() });
        } else {
          onSelect({ address: addr, lat: null, lon: null });
        }
      });
    } else {
      onSelect({ address: addr, lat: null, lon: null });
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search address, area, landmark..."
          value={query}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          className="w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 rounded-xl text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none"
        />
        {searching && <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />}
        {query && !searching && (
          <button type="button" onClick={() => { setQuery(''); onChange(''); setResults([]); setShowDropdown(false); }}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {showDropdown && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-52 overflow-y-auto">
          {results.map((item) => (
            <button key={item.place_id} type="button"
              onPointerDown={() => { selectingRef.current = true; }}
              onClick={() => handleSelect(item)}
              className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 border-b border-slate-50 last:border-0 transition-colors">
              <div className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-800">{item.structured_formatting?.main_text}</p>
                  <p className="text-xs text-slate-400">{item.structured_formatting?.secondary_text}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const IndividualProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [profile, setProfile] = useState({
    full_name: '', phone: '', email: '',
    address_line1: '', address_line2: '',
    city: '', state: '', pincode: '',
    latitude: null, longitude: null
  });
  const token = localStorage.getItem('individualToken');

  useEffect(() => {
    if (!token) { navigate('/individual/login'); return; }
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/individual/me`, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(res.data);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/individual/login'); return; }
      toast.error('Failed to load profile');
    } finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(`${API}/individual/profile`, {
        full_name: profile.full_name,
        phone: profile.phone,
        address_line1: profile.address_line1,
        address_line2: profile.address_line2,
        city: profile.city,
        state: profile.state,
        pincode: profile.pincode,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(res.data);
      localStorage.setItem('individualData', JSON.stringify(res.data));
      toast.success('Profile updated successfully');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update profile'); }
    finally { setSaving(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('individualToken');
    localStorage.removeItem('individualData');
    navigate('/individual/login');
  };

  const inputClass = "w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 rounded-xl text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const initials = profile.full_name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'U';

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <IndividualSidebar onLogout={handleLogout} />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile topbar */}
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 flex-shrink-0 z-10">
          <button onClick={() => navigate('/individual/dashboard')}
            className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 transition-colors">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="font-black text-slate-900 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>My Profile</span>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8 space-y-5">

            {/* Desktop page title */}
            <div className="hidden md:block">
              <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>My Profile</h1>
              <p className="text-sm text-slate-500 mt-0.5">Manage your personal information</p>
            </div>

            {/* Avatar banner */}
            <div className="relative bg-gradient-to-br from-orange-500 via-orange-600 to-blue-600 rounded-[2rem] px-8 py-7 text-white overflow-hidden shadow-xl shadow-orange-200">
              <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/10 rounded-full" />
              <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
              <div className="relative z-10 flex items-center gap-5">
                <div className="w-16 h-16 rounded-2xl bg-white/20 border-2 border-white/30 flex items-center justify-center text-2xl font-black flex-shrink-0" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {initials}
                </div>
                <div>
                  <p className="text-orange-100 text-xs font-medium mb-0.5">Logged in as</p>
                  <h2 className="text-xl font-black mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{profile.full_name}</h2>
                  <div className="flex items-center gap-2">
                    <HomeIcon className="w-3.5 h-3.5 text-orange-200" />
                    <span className="text-orange-100 text-sm font-medium">Individual Customer</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Individual badge card */}
            <div className="bg-white border border-slate-200/80 rounded-[2rem] px-6 py-5 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <HomeIcon className="w-6 h-6 text-orange-500" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Account Type</p>
                <p className="font-bold text-slate-800">Individual Customer</p>
                <p className="text-xs text-slate-400 mt-0.5">{profile.email}</p>
              </div>
              <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-100 rounded-xl px-3 py-1.5">
                <span className="text-[10px] font-bold text-orange-600">Active</span>
              </div>
            </div>

            {/* Edit form */}
            <div className="bg-white border border-slate-200/80 rounded-[2rem] px-6 py-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Personal Details</h3>
                  <p className="text-xs text-slate-400">Update your information below</p>
                </div>
              </div>

              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" value={profile.full_name || ''}
                      onChange={e => setProfile({ ...profile, full_name: e.target.value })}
                      required className={inputClass} />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="tel" value={profile.phone || ''}
                      onChange={e => setProfile({ ...profile, phone: e.target.value })}
                      required className={inputClass} />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="email" value={profile.email || ''}
                      readOnly
                      className="w-full pl-10 pr-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 text-sm cursor-not-allowed" />
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">Email cannot be changed. Contact support if needed.</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Address <span className="text-xs text-slate-400 font-normal">(search to update)</span></label>
                  <AddressSearch
                    value={profile.address_line1 || ''}
                    onChange={(v) => setProfile({ ...profile, address_line1: v })}
                    onSelect={({ address, lat, lon }) => setProfile({ ...profile, address_line1: address, latitude: lat, longitude: lon })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Flat/House/Floor <span className="text-xs text-slate-400 font-normal">(optional)</span></label>
                  <div className="relative">
                    <HomeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" placeholder="Flat no., building, floor, landmark..." value={profile.address_line2 || ''}
                      onChange={e => setProfile({ ...profile, address_line2: e.target.value })}
                      className={inputClass} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">City</label>
                    <input type="text" placeholder="City" value={profile.city || ''}
                      onChange={e => setProfile({ ...profile, city: e.target.value })}
                      className="w-full px-3 py-3 bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 rounded-xl text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Pincode</label>
                    <input type="text" placeholder="Pincode" value={profile.pincode || ''}
                      onChange={e => setProfile({ ...profile, pincode: e.target.value })}
                      className="w-full px-3 py-3 bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 rounded-xl text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none" />
                  </div>
                </div>

                <button type="submit" disabled={saving}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-all hover:shadow-lg hover:shadow-blue-200 hover:-translate-y-0.5 text-sm mt-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save Changes</>}
                </button>
              </form>
            </div>

            {/* Refer & Earn shortcut */}
            <button onClick={() => navigate('/individual/referrals')}
              className="w-full flex items-center gap-4 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-100 rounded-[2rem] px-6 py-4 hover:shadow-md transition-all group">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-orange-500" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-black text-slate-800 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Refer &amp; Earn</p>
                <p className="text-xs text-slate-400">Share your code · your friend gets 10%, you get 5%</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-orange-500 transition-colors" />
            </button>

            <button onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-red-100 text-red-500 hover:bg-red-50 font-semibold text-sm transition-all md:hidden">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </main>
      </div>

      <IndividualBottomNav />
    </div>
  );
};

export default IndividualProfile;
